import { Copiar } from "./Copiar";
import type { EstadoVault } from "../hooks/useVault";
import type { RedKeel } from "../config/networks";
import { EXPLORADOR, nombreDex, sitioPool, urlArkham, urlPool, urlNft, urlPosicion } from "../lib/enlaces";
import { useT } from "../lib/i18n";

/**
 * Todo lo que hace falta para comprobar por tu cuenta que esto es lo que dice ser.
 *
 * No es un adorno de transparencia: sin la direccion del vault no puedes leer el contrato en el
 * explorador, sin la del NFT no puedes ver tu posicion en Uniswap, y sin la de la logica no puedes
 * comprobar QUE CODIGO mueve tu dinero — que es justo lo que cambia entre generaciones y lo que
 * hay que mirar cuando algo no cuadra.
 */
export function Auditar({ v, red }: { v: EstadoVault; red: RedKeel }) {
  const t = useT();
  const base = EXPLORADOR[red.clave];
  const pos = urlPosicion(red, v.npm, v.tokenId, v.direccion);

  const filas: Array<{
    k: string; valor: string; url?: string; nota?: string;
    /** Segundo enlace, a la pagina de mercado. El explorador enseña el contrato; esto, el precio. */
    mercado?: { url: string; sitio: string };
  }> = [
    { k: t("Tu vault"), valor: v.direccion, url: `${base}/address/${v.direccion}`,
      nota: t("el contrato que custodia tu dinero. Es tuyo: solo tu wallet puede sacar de él.") },
    { k: t("Dueño"), valor: v.dueno, url: `${base}/address/${v.dueno}` },
    { k: t("Pool de {dex}", { dex: nombreDex(red) }), valor: v.pool, url: `${base}/address/${v.pool}`,
      nota: `${v.token0.symbol}/${v.token1.symbol} — ${t("el mercado donde trabaja tu dinero")}`,
      mercado: { url: urlPool(red, v.pool), sitio: sitioPool(red) } },
    ...(v.abierto ? [{
      k: t("NFT de la posición"), valor: `#${v.tokenId}`,
      // La ruta del NFT cambia con la familia de explorador; antes se usaba la de Blockscout en las
      // tres redes y en dos daba 404. Ver `urlNft`.
      url: urlNft(red, v.npm, v.tokenId),
      nota: t("la posición de liquidez, a nombre del vault"),
      // Solo si el DEX tiene pantalla propia para la posición. Donde no la hay, `urlPosicion` cae
      // al mismo NFT del explorador y saldrían dos enlaces al mismo sitio.
      ...(pos.url !== urlNft(red, v.npm, v.tokenId) ? { mercado: { url: pos.url, sitio: pos.sitio } } : {}),
    }] : []),
    { k: t("Lógica (rebalanceador)"), valor: v.rebalancer, url: `${base}/address/${v.rebalancer}`,
      nota: v.hayActualizacion
        ? t("hay una versión más nueva publicada — puedes adoptarla en Ajustes")
        : t("es la última que publica tu factory") },
    ...(v.impl ? [{ k: t("Implementación"), valor: v.impl, url: `${base}/address/${v.impl}`,
      nota: t("el código que se clonó al crear tu vault") }] : []),
    { k: t("Robot (operador)"), valor: v.operador, url: `${base}/address/${v.operador}`,
      nota: t("solo puede reajustar el rango. No puede mover tu dinero ni cambiar tus ajustes.") },
    { k: t("Gestor de posiciones"), valor: v.npm, url: `${base}/address/${v.npm}`,
      nota: t("el NonfungiblePositionManager de Uniswap") },
  ];

  return (
    <details className="bloque auditar">
      <summary>{t("Auditar esta piscina")} <span className="opcional">{t("direcciones")}</span></summary>
      <div className="cuerpo">
        <p className="detalle" style={{ margin: 0 }}>
          {t("Todo está en la cadena. Puedes comprobarlo tú sin fiarte de esta pantalla.")}
        </p>
        <ul className="direcciones">
          {filas.map((f) => (
            <li key={f.k}>
              <div className="et">
                <span className="k">{f.k}</span>
                {f.nota && <span className="nota">{f.nota}</span>}
              </div>
              <div className="val">
                <code>{f.valor}</code>
                <Copiar texto={f.valor} etiqueta={f.k} />
                {f.url && (
                  <a href={f.url} target="_blank" rel="noreferrer" title={t("Ver en el explorador")}>↗</a>
                )}
                {/^0x[0-9a-fA-F]{40}$/.test(f.valor) && (
                  <a className="arkham" href={urlArkham(f.valor)} target="_blank" rel="noreferrer"
                    title={t("Seguir esta dirección en Arkham")}>Arkham ↗</a>
                )}
                {f.mercado && (
                  <a
                    className="mercado"
                    href={f.mercado.url}
                    target="_blank"
                    rel="noreferrer"
                    title={t("Ver la pool en {sitio}", { sitio: f.mercado.sitio })}
                  >
                    {f.mercado.sitio} ↗
                  </a>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>
    </details>
  );
}

